import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, Phone, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from '@/components/ui/sheet';
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from '@/components/ui/navigation-menu';
import { companyInfo, navigation } from '@/data/mock';
import { cn } from '@/lib/utils';

export const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const isActive = (href) => location.pathname === href || location.pathname.startsWith(href + '/');

  return (
    <header
      className={cn(
        'fixed top-0 left-0 right-0 z-50 transition-all duration-300',
        isScrolled
          ? 'bg-white shadow-lg py-3'
          : 'bg-white/95 backdrop-blur-sm py-4'
      )}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 group">
            <div className="relative">
              <div className="w-10 h-10 bg-red-600 transform rotate-45 group-hover:rotate-[55deg] transition-transform duration-300" />
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-white font-bold text-lg transform -rotate-0">R</span>
              </div>
            </div>
            <div className="flex flex-col">
              <span className="text-xl font-bold text-gray-900 tracking-tight">{companyInfo.logo}</span>
              <span className="text-[10px] text-gray-500 tracking-widest uppercase">Civil Engineering</span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <NavigationMenu className="hidden lg:flex">
            <NavigationMenuList>
              {navigation.map((item) => (
                <NavigationMenuItem key={item.name}>
                  {item.hasDropdown ? (
                    <>
                      <NavigationMenuTrigger
                        className={cn(
                          'px-4 py-2 text-sm font-medium bg-transparent',
                          isActive(item.href)
                            ? 'text-red-600'
                            : 'text-gray-700 hover:text-red-600'
                        )}
                      >
                        {item.name}
                      </NavigationMenuTrigger>
                      <NavigationMenuContent>
                        <div className="w-[500px] p-4 bg-white">
                          <div className="grid grid-cols-2 gap-2">
                            {item.children.map((child) => (
                              <Link
                                key={child.name}
                                to={child.href}
                                className="block p-3 rounded-lg hover:bg-red-50 transition-colors group"
                              >
                                <span className="text-sm font-medium text-gray-900 group-hover:text-red-600">
                                  {child.name}
                                </span>
                              </Link>
                            ))}
                          </div>
                          <div className="mt-4 pt-4 border-t border-gray-100">
                            <Link
                              to="/services"
                              className="text-sm font-medium text-red-600 hover:text-red-700"
                            >
                              View All Services →
                            </Link>
                          </div>
                        </div>
                      </NavigationMenuContent>
                    </>
                  ) : (
                    <Link
                      to={item.href}
                      className={cn(
                        'px-4 py-2 text-sm font-medium rounded-md transition-colors duration-200 block',
                        isActive(item.href)
                          ? 'text-red-600 bg-red-50'
                          : 'text-gray-700 hover:text-red-600 hover:bg-gray-50'
                      )}
                    >
                      {item.name}
                    </Link>
                  )}
                </NavigationMenuItem>
              ))}
            </NavigationMenuList>
          </NavigationMenu>

          {/* CTA & Mobile Menu */}
          <div className="flex items-center gap-4">
            <a
              href={`tel:${companyInfo.phone}`}
              className="hidden md:flex items-center gap-2 text-sm text-gray-600 hover:text-red-600 transition-colors"
            >
              <Phone className="w-4 h-4" />
              <span>{companyInfo.phone}</span>
            </a>
            <Link to="/contact" className="hidden sm:block">
              <Button className="bg-red-600 hover:bg-red-700 text-white px-6">
                Get in Touch
              </Button>
            </Link>

            {/* Mobile Menu */}
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild className="lg:hidden">
                <Button variant="ghost" size="icon">
                  <Menu className="w-6 h-6" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[300px] sm:w-[350px] overflow-y-auto">
                <SheetTitle className="sr-only">Navigation Menu</SheetTitle>
                <div className="flex flex-col h-full pt-8">
                  <nav className="flex flex-col gap-1">
                    {navigation.map((item) => (
                      <div key={item.name}>
                        {item.hasDropdown ? (
                          <div className="mb-2">
                            <Link
                              to={item.href}
                              onClick={() => setMobileOpen(false)}
                              className={cn(
                                'px-4 py-3 text-base font-medium rounded-lg transition-colors block',
                                isActive(item.href)
                                  ? 'text-red-600 bg-red-50'
                                  : 'text-gray-700 hover:text-red-600 hover:bg-gray-50'
                              )}
                            >
                              {item.name}
                            </Link>
                            <div className="ml-4 mt-1 space-y-1">
                              {item.children.slice(0, 6).map((child) => (
                                <Link
                                  key={child.name}
                                  to={child.href}
                                  onClick={() => setMobileOpen(false)}
                                  className="block px-4 py-2 text-sm text-gray-600 hover:text-red-600 rounded-lg hover:bg-gray-50"
                                >
                                  {child.name}
                                </Link>
                              ))}
                              <Link
                                to="/services"
                                onClick={() => setMobileOpen(false)}
                                className="block px-4 py-2 text-sm text-red-600 font-medium"
                              >
                                View All Services →
                              </Link>
                            </div>
                          </div>
                        ) : (
                          <Link
                            to={item.href}
                            onClick={() => setMobileOpen(false)}
                            className={cn(
                              'px-4 py-3 text-base font-medium rounded-lg transition-colors block',
                              isActive(item.href)
                                ? 'text-red-600 bg-red-50'
                                : 'text-gray-700 hover:text-red-600 hover:bg-gray-50'
                            )}
                          >
                            {item.name}
                          </Link>
                        )}
                      </div>
                    ))}
                  </nav>
                  <div className="mt-auto pb-8">
                    <Link to="/contact" onClick={() => setMobileOpen(false)}>
                      <Button className="w-full bg-red-600 hover:bg-red-700 text-white">
                        Get in Touch
                      </Button>
                    </Link>
                    <a
                      href={`tel:${companyInfo.phone}`}
                      className="flex items-center justify-center gap-2 mt-4 text-gray-600"
                    >
                      <Phone className="w-4 h-4" />
                      <span>{companyInfo.phone}</span>
                    </a>
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
